import type { DetectedPlugin, PluginNameEntry } from '../types';
import pluginNamesData from '../data/pluginNames.json';

const pluginNames = pluginNamesData as PluginNameEntry[];

// Minimum character length for a term to be used in substring/word matching.
// Terms shorter than this require an exact whole-string match.
const MIN_SUBSTRING_LEN = 5;

// Build a flat lookup: lowercase search term → canonical entry
interface LookupEntry {
  canonical: PluginNameEntry;
}

const lookup = new Map<string, LookupEntry>();

for (const entry of pluginNames) {
  lookup.set(entry.name.toLowerCase(), { canonical: entry });
  for (const alias of entry.aliases ?? []) {
    lookup.set(alias.toLowerCase(), { canonical: entry });
  }
}

/**
 * Returns true if `term` appears as a whole word inside `str`.
 * Word boundary = start/end of string or a non-alphanumeric character.
 */
function matchesWholeWord(str: string, term: string): boolean {
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(?<![a-z0-9])${escaped}(?![a-z0-9])`, 'i');
  return re.test(str);
}

/**
 * Cross-reference extracted strings against the plugin names database.
 * Uses whole-word boundary matching to avoid false positives from short
 * terms like "RX" matching inside unrelated words.
 */
export function matchPluginNames(strings: string[]): DetectedPlugin[] {
  const found: DetectedPlugin[] = [];
  const seen = new Set<string>();

  for (const str of strings) {
    const lower = str.toLowerCase();

    for (const [term, entry] of lookup) {
      let matched = false;

      if (lower === term) {
        // Exact whole-string match — always valid
        matched = true;
      } else if (term.length >= MIN_SUBSTRING_LEN) {
        // Only do substring/word matching for terms long enough to be unambiguous
        matched = matchesWholeWord(lower, term);
      }
      // Short terms (< MIN_SUBSTRING_LEN) are skipped unless exact-match above

      if (matched) {
        const canonicalName = entry.canonical.name;
        if (!seen.has(canonicalName)) {
          seen.add(canonicalName);
          const exactMatch = lower === term;
          found.push({
            name: canonicalName,
            vendor: entry.canonical.vendor,
            pluginType: 'unknown',
            confidence: exactMatch ? 0.72 : 0.55,
            detectionMethod: 'heuristic',
          });
        }
      }
    }
  }

  return found;
}
